import { useState, useEffect, useCallback } from "react";
import axiosInstance from '../../../Config/axiosInstance';
import {Container, Paper, Table, TableBody, TableCell, TableContainer,TableHead, TableRow, Button, Typography, CircularProgress,
  Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select, Checkbox, FormControl, InputLabel, MenuItem } from '@mui/material';
import { Save, PictureAsPdf, GetApp, Assessment} from '@mui/icons-material';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './ProjectExpensesPerCostsElements.css';
import logoImg from '../../../Images/logo_pmpc.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ProjectExpensesPerCostsElements = () => {
  const [filters, setFilters] = useState({ projectCode: '' });
  const [projectCodeOptions, setProjectCodeOptions] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);

  const loadProjectCodeOptions = useCallback(async () => {
    setLoadingOptions(true);
    setError('');
    try {
      const response = await axiosInstance.get('/project-expense-cost-element/');
      const options = response.data.map(code => ({ value: code, label: code }));
      setProjectCodeOptions(options);
    } catch (err) {
      console.error("Error loading project codes:", err);
      setError('Error loading project codes');
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => { 
    loadProjectCodeOptions(); 
  }, [loadProjectCodeOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!filters.projectCode) {
      setError('Please select a Project Code');
      setReportData(null);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/project-expense-cost-element/', {
        params: { project_code: filters.projectCode }
      });

      if (!response.data) {
        throw new Error("No data received from server");
      }

      setReportData(response.data);
      setReportGenerated(true);
    } catch (err) {
      console.error("Error generating report:", err);
      setError(`Error generating report: ${err.message || 'Unknown error'}`);
      setReportData(null);
      setReportGenerated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const formatNumber = (num) => {
    if (num == null || num === undefined || num === '' || isNaN(Number(num))) {
      return '0.00';
    }
    const number = typeof num === 'string' ? parseFloat(num) : num;
    return new Intl.NumberFormat('en-EN', { 
      maximumFractionDigits: 2, 
      minimumFractionDigits: 2 
    }).format(number);
  };

  //functie pentru obtinerea tipurilor de resurse aferente codurilor de proiect
  const getAllInternalResources = () => {
    if (!reportData || !reportData.cost_elements || !Array.isArray(reportData.cost_elements)) {
      return [];
    }
    
    const allResources = new Set();
    
    reportData.cost_elements.forEach((costElement) => {
      if (costElement.internal_costs && typeof costElement.internal_costs === 'object') {
        Object.keys(costElement.internal_costs).forEach(resource => {
          allResources.add(resource);
        });
      }
    });
    
    const resourcesArray = Array.from(allResources).sort();
    return resourcesArray;
  };

  //se calculeaza totalurile pentru fiecare tip de resursa interna
  const getInternalResourceTotals = () => {
    if (!reportData || !reportData.cost_elements) {
      return {};
    }
    
    const totals = {};
    const resources = getAllInternalResources();
    
    //se initializeaza totalurile cu 0
    resources.forEach(resource => {
      totals[resource] = 0;
    });
    
    //calculam totalurile pentru fiecare tip de resursa in parte
    reportData.cost_elements.forEach(costElement => {
      if (costElement.internal_costs && typeof costElement.internal_costs === 'object') {
        Object.entries(costElement.internal_costs).forEach(([resource, cost]) => {
          if (totals[resource] !== undefined) {
            totals[resource] += Number(cost) || 0;
          }
        });
      }
    });
    
    return totals;
  };

  //calcul total costuri interne
  const getTotalInternalCost = () => {
    const totals = getInternalResourceTotals();
        return Object.values(totals).reduce(
            (sum, val) => sum + (Number(val) || 0),
            0
        );
    }; 

  //calcul total facturi externe
  const getExternalInvoicesTotal = () => {
    if (!reportData || !reportData.cost_elements) {
      return 0;
    }
    
    let total = 0;
    reportData.cost_elements.forEach(costElement => {
      if (costElement.external_invoices_total) {
        total += Number(costElement.external_invoices_total) || 0;
      }
    });
    
    return total;
  };

  //calcul total
  const getCostElementTotal = (costElement) => {
    let total = 0;
    
    //adauga resursele interne
    if (costElement.internal_costs && typeof costElement.internal_costs === 'object') {
      Object.values(costElement.internal_costs).forEach(cost => {
        total += Number(cost) || 0;
      });
    }
    
    //adauga externa resource
    if (costElement.external_invoices_total) {
      total += Number(costElement.external_invoices_total) || 0;
    }
    
    return total;
  };

    //totalul general
    const getGrandTotal = () => {
        const internalTotals = getInternalResourceTotals();
        const internalTotal = Object.values(internalTotals).reduce((sum, val) => sum + (Number(val) || 0), 0);
        const externalTotal = getExternalInvoicesTotal();
        
        return internalTotal + externalTotal;
    };

    const exportToExcel = async () => {
      if (!reportData || !reportData.cost_elements || reportData.cost_elements.length === 0) {
        alert('No data to export!');
        return;
      }

      try {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Project Expenses Report');

        const totalColumns = 2 + internalResources.length + 2;
        sheet.mergeCells('A1:B4');
        
        const lastColumnLetter = String.fromCharCode(65 + totalColumns - 1);
        sheet.mergeCells(`C1:${lastColumnLetter}4`);
        
        try {
          const response = await fetch(logoImg);
          const imageBuffer = await response.arrayBuffer();
          
          const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: 'png',
          });
          
          sheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            br: { col: 2, row: 4 }
          });
        } catch (error) {
          console.warn('Logo could not be loaded:', error);
        }
        
        const titleCell = sheet.getCell('C1');
        titleCell.value = 'Project Expenses Per Costs Elements Report';
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        sheet.addRow([]);
        
        // Linii corectate - fără variabile neutilizate
        sheet.addRow(['Project Code:', reportData.project_code || '']);
        sheet.addRow(['Project Description:', reportData.project_description || '']);
        sheet.addRow([]);
        
        const headers = [
          'Cost Element ID',
          'Description Cost Element',
          ...internalResources.map(resource => `Internal Cost ${resource}`),
          'External Cost',
          'Total per Cost Element'
        ];
        
        const headerRow = sheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
        headerRow.height = 25;
        
        for (let i = 1; i <= headers.length; i++) {
          headerRow.getCell(i).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE8E8E8' }
          };
        }
        
        reportData.cost_elements.forEach((row) => {
          const costElementTotal = getCostElementTotal(row);
          const rowData = [
            row.cost_element_id,
            row.cost_element_description,
            ...internalResources.map(resource => formatNumber(row.internal_costs?.[resource] || 0)),
            formatNumber(row.external_invoices_total || 0),
            formatNumber(costElementTotal)
          ];
          
          const dataRow = sheet.addRow(rowData);
          
          internalResources.forEach((_, index) => {
            const cell = dataRow.getCell(3 + index);
            cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: 'right' };
          });
          
          const externalCell = dataRow.getCell(3 + internalResources.length);
          externalCell.numFmt = '#,##0.00';
          externalCell.alignment = { horizontal: 'right' };
          
          const totalCell = dataRow.getCell(3 + internalResources.length + 1);
          totalCell.numFmt = '#,##0.00';
          totalCell.font = { bold: true };
          totalCell.alignment = { horizontal: 'right' };
        });

        sheet.addRow([]);
        
        const totalAdicional = formatNumber(reportData?.cost_aditional_total || 0);
        const totalInternal = formatNumber(getTotalInternalCost());
        const totalExternal = formatNumber(getExternalInvoicesTotal());
        const grandTotal = formatNumber(getGrandTotal());

        const totalsRowData = [''];
        
        totalsRowData.push(`Total Aditional Cost: \n${totalAdicional}`);
        
        for (let i = 0; i < internalResources.length; i++) {
          if (i === 0) {
            totalsRowData.push(`Total Internal Cost: \n${totalInternal}`);
          } else {
            totalsRowData.push(''); 
          }
        }
        
        totalsRowData.push(`Total External Cost: \n${totalExternal}`);
        totalsRowData.push(`Total Project Cost: \n${grandTotal}`);
        const totalsRow = sheet.addRow(totalsRowData);
        totalsRow.font = { bold: true };
        const lastColumnWithValue = totalsRowData.length;
        
        for (let colIndex = 1; colIndex <= lastColumnWithValue; colIndex++) {
          totalsRow.getCell(colIndex).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFCDF5E0' }
          };
        }
        
        totalsRow.getCell(1).alignment = {
          wrapText: true,
          vertical: 'middle',
          horizontal: 'center'
        };
        
        const valueCells = [
          2, 
          3, 
          2 + internalResources.length + 1,    
          2 + internalResources.length + 2  
        ];
        
        valueCells.forEach(cellIndex => {
          const cell = totalsRow.getCell(cellIndex);
          cell.alignment = { 
            wrapText: true,
            vertical: 'middle',
            horizontal: 'center'
          };
          cell.numFmt = '#,##0.00';
        });
        
        for (let colIndex = 4; colIndex < 2 + internalResources.length + 1; colIndex++) {
          const cell = totalsRow.getCell(colIndex);
          if (cell.value === '') {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          }
        }
        
        totalsRow.height = 40;
        
        sheet.columns.forEach((column, columnIndex) => {
          let maxLength = 0;
          let maxLines = 1;
          
          column.eachCell({ includeEmpty: true }, (cell) => {
            let cellValue = '';
            
            if (cell.value !== null && cell.value !== undefined) {
              cellValue = cell.value.toString();
            }
            
            const textLength = cellValue.length;
            
            if (cell.row === totalsRow.number && cell.value && cell.value.includes('\n')) {
              const lines = cell.value.split('\n');
              const maxLineLength = Math.max(...lines.map(line => line.length));
              maxLength = Math.max(maxLength, maxLineLength);
              maxLines = Math.max(maxLines, lines.length);
            } else if (cell.font && cell.font.bold) {
              maxLength = Math.max(maxLength, Math.min(textLength, 20));
            } else {
              maxLength = Math.max(maxLength, textLength);
            }
          });
          
          let columnWidth;
          
          if (columnIndex === 0) {
            columnWidth = Math.max(maxLength, 15);
          } else if (columnIndex === 1) {
            columnWidth = Math.max(maxLength, 50); 
          } else if (columnIndex >= 2 && columnIndex < 2 + internalResources.length) {
            columnWidth = Math.max(maxLength, 15);
          } else if (columnIndex === 2 + internalResources.length) {
            columnWidth = Math.max(maxLength, 15);
          } else if (columnIndex === 2 + internalResources.length + 1) {
            columnWidth = Math.max(maxLength, 18);
          } else {
            columnWidth = Math.max(maxLength, 12);
          }
          
          columnWidth = Math.min(columnWidth, 25);
          column.width = columnWidth;
        });
        
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        const fileName = `Project_Expenses_${reportData.project_code || 'Report'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        
        if (typeof saveAs !== 'undefined') {
          saveAs(blob, fileName);
        }
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        alert('Error exporting to Excel. Please try again.');
      }
    };

    const exportPDF = async () => {
      if (!reportData || !reportData.cost_elements || reportData.cost_elements.length === 0) {
          alert('No data to export!');
          return;
      }
      
      //pdf portrait
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      //logo
      try {
          const response = await fetch(logoImg);
          const imageBlob = await response.blob();
          const imageUrl = URL.createObjectURL(imageBlob);
          pdf.addImage(imageUrl, "PNG", 8, 3, 28, 8);
          setTimeout(() => URL.revokeObjectURL(imageUrl), 1000);
      } catch (error) {
          console.warn('Logo could not be loaded:', error);
      }

      //dimensiuni elemente decorative
      const triangleWidth = 20;
      const totalWidth = pdfWidth * 0.25;
      const barWidth = totalWidth - triangleWidth;

      const barHeight = 1.2;     // bara scurtă + triunghi
      const secondBarHeight = 2; // bara lungă
      const barTop = 15;
      const barBottom = barTop + barHeight;

      //bara scurta
      pdf.setFillColor(23, 52, 88);
      const barOverlap = 1;
      pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

      //triunghi
      pdf.triangle(
          barWidth, barBottom,                 
          barWidth + triangleWidth, barBottom,  
          barWidth, barTop,                   
          "F"
      );

      //suprapunere 
      const overlap = 0.6;
      const headerLongBarTop = barBottom - overlap;

      //bara lunga full-width (header)
      pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

      const dottedLineY = headerLongBarTop + secondBarHeight;
      
      //titlu
      const reportTitle = `Project Expenses Per Costs Elements Report`;
      
      //detalii proiect
      const projectCode = `Project Code: ${reportData.project_code}`
      const project_description = `Project Description: ${reportData.project_description}`;

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text(reportTitle, pdfWidth / 2, dottedLineY + 8, {align: "center"});
      
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      pdf.text(projectCode, pdfWidth / 2, dottedLineY + 14, {align: "center"});

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      pdf.text(project_description, pdfWidth / 2, dottedLineY + 20, {align: "center"});

      const contentTop = dottedLineY + 26;
      let currentY = contentTop;

      //calculeaza latimile coloanelor
      const numInternalResources = internalResources.length;
      const numColumns = 2 + numInternalResources + 2;
      
      const availableWidth = pdfWidth - 20;
      const columnWidth = availableWidth / numColumns;
      
      //configuratie coloane
      const columnStyles = {
          0: { cellWidth: columnWidth * 0.6},
          1: { cellWidth: columnWidth * 1.8 }, 
      };
      
      //resurse interne
      for (let i = 2; i < 2 + numInternalResources; i++) {
          columnStyles[i] = { cellWidth: columnWidth * 0.9, halign: 'right' };
      }
      
      //ultimele 2 coloane
      columnStyles[2 + numInternalResources] = { cellWidth: columnWidth, halign: 'right' }; // External
      columnStyles[2 + numInternalResources + 1] = { cellWidth: columnWidth * 1.1, halign: 'right', fontStyle: 'bold' }; // Total

      //headers pentru tabel
      const tableHeaders = [
          ["Cost ID", "Description Cost Element", 
          ...internalResources.map(resource => `Internal Cost\n${resource}`), 
          "External Cost", "Total per\nCost Element"]
      ];

      //datele pentru tabel 
      const tableData = reportData.cost_elements.map(row => {
          const costElementTotal = getCostElementTotal(row);
          const rowData = [
              row.cost_element_id,
              row.cost_element_description,
              ...internalResources.map(resource => formatNumber(row.internal_costs?.[resource] || 0)),
              formatNumber(row.external_invoices_total || 0),
              formatNumber(costElementTotal)
          ];
          return rowData;
      });

      autoTable(pdf, {
          startY: currentY,
          head: tableHeaders,
          body: tableData,
          styles: {
              fontSize: 8,
              cellPadding: 2,
              overflow: "linebreak",
              lineColor: [180, 180, 180],
              lineWidth: 0.25,
              valign: "middle",
              textColor: [0, 0, 0],
              minCellHeight: 6
          },
          headStyles: {
              fillColor: [232, 232, 232],
              textColor: 0, 
              fontStyle: "bold",
              halign: "center",
              valign: "middle"
          },
          columnStyles: columnStyles,
          alternateRowStyles: {
              fillColor: [245, 247, 250]
          },
          margin: {left: 5, right: 5},
          tableWidth: 'auto',
          theme: 'grid',
          didParseCell: function(data) {
              if (data.section === 'body') {
                  if (data.column.index >= 2) {
                      data.cell.styles.halign = 'right';
                  }
              }
          },
          didDrawPage: function(data) {
              if (data.cursor) {
                  currentY = data.cursor.y;
              }
          }
      });
      
      currentY = pdf.lastAutoTable.finalY;

      //randul  cu totaluri
      const totalAdicional = formatNumber(reportData?.cost_aditional_total || 0);
      const totalInternal = formatNumber(getTotalInternalCost());
      const totalExternal = formatNumber(getExternalInvoicesTotal());
      const grandTotal = formatNumber(getGrandTotal());

      //creaza randul cu totaluri
      const totalsRow = ['Costs:', `Total Aditional:\n${totalAdicional}`];
      
      for (let i = 0; i < numInternalResources; i++) {
          if (i === 0) {
              totalsRow.push(`Total Internal:\n${totalInternal}`);
          } else {
              totalsRow.push(''); 
          }
      }
      
      totalsRow.push(`Total External:\n${totalExternal}`);
      totalsRow.push(`Total Project:\n${grandTotal}`);

      //adauga randul cu totaluri
      autoTable(pdf, {
          startY: currentY,
          body: [totalsRow],
          styles: {
              fontSize: 9,
              cellPadding: 2,
              lineColor: [180, 180, 180],
              lineWidth: 0.25,
              valign: "middle",
              textColor: [0, 0, 0],
              fillColor: [205, 245, 224] 
          },
          columnStyles: columnStyles,
          margin: {left: 5, right: 5},
          tableWidth: 'auto',
          theme: 'grid',
          didParseCell: function(data) {
              if (data.column.index > 0) {
                  data.cell.styles.halign = 'center';
                  data.cell.styles.valign = 'middle';
              }
              
              if (data.column.index === 2 + numInternalResources + 1) {
                  data.cell.styles.halign = 'right';
              }
          }
      });

      //footer
      const totalPages = pdf.internal.getNumberOfPages();
      
      for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          
          pdf.setFillColor(23, 52, 88);

          const bottomOffset = 12; 
          const longBarTop = pdfHeight - bottomOffset; 

          //suprapunere
          const verticalOverlapFooter = 0.6;

          //bara lunga jos
          pdf.rect(
              0,
              longBarTop - verticalOverlapFooter + 0.1,
              pdfWidth,
              secondBarHeight,
              "F"
          );

          //bara scurta + triunghi
          const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
          const shortBarBottom = shortBarTop + barHeight;

          //bara scurta 
          const barOverlapFooter = 1;
          pdf.rect(
              0,
              shortBarTop,
              barWidth + barOverlapFooter,
              barHeight,
              "F"
          );

          //triunghiul din partea dreapta jos
          pdf.triangle(
              barWidth, shortBarTop,                    
              barWidth + triangleWidth, shortBarTop,        
              barWidth, shortBarBottom,                      
              "F"
          );

          //footer text
          pdf.setFontSize(7);
          pdf.setFont("helvetica", "normal");
          pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
          const today = new Date();
          const currentDate = today.toLocaleDateString("ro-RO");
          pdf.text(`${currentDate} `, pdfWidth - 30, pdfHeight - 3);
          pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
      }

      //save pdf
      pdf.save(`ProjectExpensesPerCostsElements_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const internalResources = getAllInternalResources();
    const externalInvoicesTotal = getExternalInvoicesTotal();

    return (
    <Container maxWidth={false} disableGutters className="rep-exp-costs exp-costs">
      <Container maxWidth={false} disableGutters className="exp-cost-container">
        <Typography variant="h4" gutterBottom marginTop='20px'>
          Project Expenses Per Costs Elements
        </Typography>

        <Paper className="filters-paper">
          <form onSubmit={handleSubmit}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <FormControl
                  size="small"
                  sx={{ minWidth: 250 }}
                  required
                  disabled={loadingOptions || projectCodeOptions.length === 0}
                >
                  <InputLabel id="project-code-label">Project Code</InputLabel>
                  <Select
                    labelId="project-code-label"
                    value={filters.projectCode}
                    onChange={(e) =>
                      setFilters(prev => ({ ...prev, projectCode: e.target.value }))
                    }
                    displayEmpty
                    input={<OutlinedInput label="Project Code" />}
                    renderValue={(selected) =>
                      selected ? selected : ''
                    }
                  >
                    {loadingOptions && (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                      </MenuItem>
                    )}
                    {projectCodeOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={filters.projectCode === option.value} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  className="generate-button"
                  startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                  disabled={loadingOptions || loading}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </Button>
              </Box>

              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleClick}
                  className="save-button"
                  startIcon={<Save />}
                  disabled={!reportGenerated}
                >
                  Save As
                </Button>

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem onClick={() => {exportPDF(); handleClose();}}>
                    <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                    <ListItemText>Save as PDF</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { exportToExcel(); handleClose(); }}>
                    <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                    <ListItemText>Save as Excel</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" className="error-alert" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </form>
        </Paper>

        {reportGenerated && reportData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
            <Paper className="filters-paper" sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="textSecondary">
                Project Code: {reportData.project_code}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Description: {reportData.project_description}
              </Typography>

              {reportData.cost_elements && reportData.cost_elements.length > 0 ? (
                <>
                  <TableContainer className="report-table-container" sx={{ mt: 3, maxHeight: 500, overflow: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 800 }}>
                      <TableHead>
                        <TableRow className="table-header">
                          <TableCell className="table-header-cell">Cost Element ID</TableCell>
                          <TableCell className="table-header-cell">Description Cost Element</TableCell>
                          {internalResources.map((resource, index) => (
                            <TableCell key={index} className="table-header-cell">
                                {`Internal Cost ${resource}`}
                            </TableCell>
                          ))}
                          <TableCell className="table-header-cell" >External Cost</TableCell>
                          <TableCell className="table-header-cell" sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#1565c0' }}>
                            Total per Cost Element
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.cost_elements.map((row, rowIndex) => {
                          const costElementTotal = getCostElementTotal(row);
                          
                          return (
                            <TableRow 
                              key={rowIndex}>
                              <TableCell>
                                {row.cost_element_id}
                              </TableCell>
                              <TableCell>
                                {row.cost_element_description}
                              </TableCell>
                              
                              {internalResources.map((resource, idx) => (
                                <TableCell key={idx} >
                                  {formatNumber(row.internal_costs?.[resource] || 0)}
                                </TableCell>
                              ))}
                              <TableCell >
                                {formatNumber(row.external_invoices_total || 0)}
                              </TableCell>
                              <TableCell>
                                {formatNumber(costElementTotal)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    
                        <TableRow sx={{ backgroundColor: '#cdf5e0ff' }}>
                        <TableCell colSpan={1}>
                            <Typography fontWeight="bold">TOTALS</Typography>
                        </TableCell>

                        <TableCell>
                        <Typography variant="body2">Total Aditional Cost:</Typography>
                        <Typography fontWeight="bold">
                            {formatNumber(reportData?.cost_aditional_total)} RON
                        </Typography>
                        </TableCell>

                        <TableCell colSpan={internalResources.length}>
                            <Typography variant="body2">Total Internal Cost:</Typography>
                            <Typography fontWeight="bold">
                            {formatNumber(getTotalInternalCost())}
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography variant="body2">Total External Cost:</Typography>
                            <Typography fontWeight="bold">
                            {formatNumber(externalInvoicesTotal)}
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography variant="body2">Total Cost Per Project:</Typography>
                            <Typography fontWeight="bold">
                            {formatNumber(getGrandTotal())}
                            </Typography>
                        </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No cost elements found for this project.
                </Alert>
              )}
            </Paper>
          </Box>
        )}

        {reportGenerated && !reportData && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Report generated but no data available to display.
          </Alert>
        )}

        {reportGenerated && reportData && (
          <Box className="report-footer">
            <Typography variant="caption" className="footer-text">
              Generated on {reportData.report_date || new Date().toLocaleString('ro-RO')} |{' '}
              Project Code: {reportData.project_code}
            </Typography>
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default ProjectExpensesPerCostsElements;